'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

declare global {
  interface Window {
    turnstile: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  siteKey?: string;
}

const TURNSTILE_SCRIPT_SELECTOR =
  'script[src*="challenges.cloudflare.com/turnstile"]';

function waitForTurnstileApi(onReady: () => void): (() => void) | void {
  if (window.turnstile) {
    onReady();
    return;
  }

  const interval = window.setInterval(() => {
    if (window.turnstile) {
      window.clearInterval(interval);
      onReady();
    }
  }, 50);

  return () => window.clearInterval(interval);
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  ({ onVerify, onError, onExpire, siteKey }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    const onVerifyRef = useRef(onVerify);
    const onErrorRef = useRef(onError);
    const onExpireRef = useRef(onExpire);

    useEffect(() => {
      onVerifyRef.current = onVerify;
      onErrorRef.current = onError;
      onExpireRef.current = onExpire;
    }, [onVerify, onError, onExpire]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      const markLoaded = () => setScriptLoaded(true);

      if (document.querySelector(TURNSTILE_SCRIPT_SELECTOR)) {
        return waitForTurnstileApi(markLoaded);
      }

      const script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;

      window.onTurnstileLoad = markLoaded;
      document.head.appendChild(script);

      return () => {
        // Keep script loaded globally for other widgets on the page.
      };
    }, []);

    useEffect(() => {
      if (!scriptLoaded || !containerRef.current || !siteKey) return;
      if (widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onVerifyRef.current(token),
        'error-callback': () => onErrorRef.current?.(),
        'expired-callback': () => onExpireRef.current?.(),
      });

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [scriptLoaded, siteKey]);

    return <div ref={containerRef} className="mt-2 flex justify-center" />;
  }
);

Turnstile.displayName = 'Turnstile';
